import React, { useMemo } from "react";

import {
    DeleteOutline as DeleteIcon,
    EditOutlined as EditIcon,
} from "@mui/icons-material";
import {
    Box,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";

import { connectionSectionDeleteButtonSx } from "../../shared/connectionSectionActionStyles";
import { DynamicField } from "../DynamicFieldsSection.types";
import { parseFormulaTokens } from "../utils/formulaTokens";
import FormulaTokenRow from "./FormulaTokenRow";

const ACCENT = "#ed6c02";

interface DynamicFieldCardProps {
    field: DynamicField;
    onEdit: (field: DynamicField) => void;
    onDelete: (field: DynamicField) => void;
}

const formatCreatedAt = (createdAt: string): string => {
    if (!createdAt) return "";
    const date = new Date(createdAt);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
};

const DynamicFieldCard: React.FC<DynamicFieldCardProps> = ({
    field,
    onEdit,
    onDelete,
}) => {
    const isBasic = field.type === "basic_mode";
    const createdAt = formatCreatedAt(field.created_at);
    const tokens = useMemo(() => parseFormulaTokens(field), [field]);

    return (
        <Box
            sx={{
                px: "16px",
                py: "14px",
                borderRadius: "10px",
                backgroundColor: "#fff",
                border: "1px solid rgba(0, 0, 0, 0.05)",
                boxShadow: "none",
                transition: "background-color 0.15s ease, border-color 0.15s ease",
                "&:hover": {
                    backgroundColor: "#fafafa",
                    borderColor: "#e5e5ea",
                },
            }}
        >
            <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                gap={1.5}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={1.25}
                        flexWrap="wrap"
                    >
                        <Typography
                            noWrap
                            title={field.name}
                            sx={{
                                fontWeight: 700,
                                fontSize: "16px",
                                lineHeight: 1.3,
                                fontFamily: '"Nunito", sans-serif',
                                color: "#1a1a1a",
                                maxWidth: "100%",
                            }}
                        >
                            {field.name}
                        </Typography>
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={0.6}
                            sx={{ flexShrink: 0 }}
                        >
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: isBasic
                                        ? "rgba(0, 0, 0, 0.28)"
                                        : ACCENT,
                                }}
                            />
                            <Typography
                                sx={{
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    letterSpacing: "0.02em",
                                    color: isBasic ? "#6B6B6B" : ACCENT,
                                }}
                            >
                                {isBasic ? "Simple" : "Advanced"}
                            </Typography>
                        </Stack>
                    </Stack>

                    <FormulaTokenRow tokens={tokens} />

                    {createdAt && (
                        <Typography
                            sx={{
                                mt: 0.75,
                                fontSize: "11px",
                                color: "#A0A0A0",
                                lineHeight: 1.3,
                            }}
                        >
                            Edited {createdAt}
                        </Typography>
                    )}
                </Box>

                <Stack
                    direction="row"
                    gap={0.25}
                    flexShrink={0}
                    sx={{ mt: -0.25 }}
                >
                    <Tooltip title="Edit dynamic field">
                        <IconButton
                            size="small"
                            onClick={() => onEdit(field)}
                            aria-label="Edit dynamic field"
                            sx={{
                                color: "#4A4A4A",
                                "&:hover": {
                                    color: "#1a1a1a",
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                },
                            }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete dynamic field">
                        <IconButton
                            size="small"
                            onClick={() => onDelete(field)}
                            aria-label="Delete dynamic field"
                            sx={connectionSectionDeleteButtonSx}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>
        </Box>
    );
};

export default DynamicFieldCard;
